import apiClient from './apiClient';
import axios from 'axios';
import { notifyAuthChanged } from './hooks/useAuth';

// Base URL used by the forgot-password calls that go through plain axios
// (they don't use apiClient because the user isn't logged in yet).
const PORTAL_BASE_URL = 'https://stm-dev.intentio.co.za/api/portal/';

// ✅ Helper function to extract detailed error messages from backend response
const getErrorMessage = (error) => {
  try {
    const data = error?.response?.data;

    // Try to get detail first (validation errors)
    if (data?.detail) {
      if (Array.isArray(data.detail)) {
        return data.detail.map((d) => d.msg || JSON.stringify(d)).join("; ");
      }
      return data.detail;
    }

    // Try to get message field
    if (data?.message) {
      return data.message;
    }

    // Try to get error field
    if (data?.error) {
      return data.error;
    }

    // Fall back to error message
    if (error?.message) {
      return error.message;
    }

    return "An unknown error occurred. Please try again.";
  } catch (e) {
    return "An error occurred while processing your request.";
  }
};

// Function to fetch fibre plans
export const fetchFibrePlans = async () => {
  try {
    const response = await apiClient.get('/products');
    return response.data.data.map((plan) => ({
      speed: `${plan.speed_down_mbps} Mbps`,
      price: `R${plan.recurring_price_incl}`,
      activationFee: `R${plan.onceoff_price_incl}`,
      monthlyPayment: `R${plan.recurring_price_incl}`,
      totalPayment: `R${plan.recurring_price_incl + plan.onceoff_price_incl}`,
      description: plan.description,
      available: plan.available,
      id: plan.id,
      productType: plan.product_type,
      name: plan.name,
      recurringDescription: plan.recurring_description,
      onceoffDescription: plan.onceoff_description,
      currency: plan.currency,
      renewalType: plan.renewal_type,
      durationDays: plan.duration_days,
    }));
  } catch (error) {
    console.error('Error fetching fibre plans:', error);
    throw new Error('Error fetching data');
  }
};


// Function to register a new user
export const registerUser = async (userData) => {
  try {
    const response = await apiClient.post('/user/register', userData);
    if (response.data.success) {
      console.log('User registered successfully:', response.data.data);
      return response.data.data;
    } else {
      console.error('User registration failed:', response.data.message);
      throw new Error(response.data.message);
    }
  } catch (error) {
    console.error('Error during user registration:', error);
    throw error;
  }
};



// Function to get Portal Files : Identity and Address Files
export const uploadPortalFile = async (portalEndCustomerId, documentType, file) => {
  try {
    // Construct the URL with query parameters
    const url = `/user/upload-file?portal_end_customer_id=${encodeURIComponent(portalEndCustomerId)}&document_type=${encodeURIComponent(documentType)}`;

    // Create a FormData object to handle the file upload
    const formData = new FormData();
    formData.append('file', file);
    console.log("Uploading file:", file.name);


    // Send a POST request with the FormData
    const response = await apiClient.post(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data', // Specify the content type for file uploads
      },
    });

    // Check if the response indicates success
    if (response.data.success) {
      console.log('File uploaded successfully:', response.data.data);
      return {
        success: true,
        data: response.data.data, // Return the uploaded file details
      };
    } else {
      console.error('File upload failed:', response.data.message);
      return {
        success: false,
        message: response.data.message,
      };
    }
  } catch (error) {
    console.error('Error during file upload:', error);
    return {
      success: false,
      message: getErrorMessage(error),
    };
  }
};



// Function to get Portal Files : Identity and Address Files
export const getPortalFiles = async (portalEndCustomerId) => {
  try {
    const response = await apiClient.get(`/user/files/${portalEndCustomerId}`);

    if (response.data.success) {
      return {
        success: true,
        data: response.data.data, // <- Ensure this is correct
      };
    } else {
      return {
        success: false,
        message: response.data.message || 'Unexpected error fetching files',
      };
    }
  } catch (error) {
    console.error('Error in getPortalFiles:', error);
    return {
      success: false,
      message: getErrorMessage(error),
    };
  }
};





// Function to generate confirmation details
export const generateConfirmationDetails = (registeredUser, formData) => {
  const details = {
    portal_end_customer_id: registeredUser.id,
    confirmation_email: registeredUser.email,
    confirmation_mobile_number: registeredUser.mobile_number,
    confirmation_pin: 0, // Placeholder for PIN
  };
  console.log('Generated Confirmation Details:', details);
  return details;
};


// Function to get confirmation details with OTP from modal
export const getConfirmationDetails = (confirmationDetails, setModalSubmitHandler) => {
  return new Promise((resolve) => {
    const handleConfirm = (otp) => {
      resolve({
        ...confirmationDetails,
        confirmation_pin: otp, // Update with OTP entered by the user
      });
    };

    // Set the modal submit handler dynamically
    setModalSubmitHandler(() => handleConfirm);
  });
};

 //Function to confirm user using generated details
export const confirmUser = async (confirmationDetails) => {
  console.log('Payload sent to confirmation API:', confirmationDetails);

  try {
    const response = await apiClient.post('/user/confirmation', confirmationDetails);

    if (response.data.success) {
      console.log('User confirmed successfully:', response.data.message);
      return response.data.message;
    } else {
      console.error('User confirmation failed:', response.data.message);
      throw new Error(response.data.message);
    }
  } catch (error) {
    console.error('Error during user confirmation:', error);
    throw error;
  }
};


//Generate subscription details
export const generateSubscriptionDetails = (registeredUser, formData, packageDetails) => {
  console.log('Checking if portal_end_customer_id is passed correctly:', registeredUser.portal_end_customer_id);
  return {
    portal_end_customer_id: registeredUser.portal_end_customer_id,
    portal_product_id: packageDetails.id,
    preferred_payment_method: formData.preferred_payment_method,
    billing_auto_renew: formData.billing_auto_renew,
    application_type: formData.application_type,
    subscription_initialisation_type: formData.subscription_initialisation_type,
    total_cost: packageDetails.totalPayment,
    activation_fee: packageDetails.activationFee,
    monthly_cost: packageDetails.monthlyPayment,
    duration_days: packageDetails.durationDays,
    speed: packageDetails.speed,
    product_name: packageDetails.name,
  };
};


// Function to create a subscription
export const createSubscription = async (subscriptionData) => {
  try {
    const response = await apiClient.post('/subscriptions/create', subscriptionData);
    if (response.data.success) {
      console.log('Subscription created successfully:', response.data.data);
      if (response.data.paymentRedirect) {
        window.location.href = response.data.paymentRedirect; // Redirect to payment portal
      }
      return response.data.data;
    } else {
      console.error('Subscription creation failed:', response.data.message);
      throw new Error(response.data.message);
    }
  } catch (error) {
    console.error('Error during subscription creation:', error);
    throw error;
  }
};


// Function to create subscription with Pay Later option
export const createSubscriptionPayLater = async (subscriptionData) => {
  try {
    const response = await apiClient.post('/subscriptions/create', subscriptionData);

    if (response.data.success) {
      console.log('Subscription created successfully (Pay Later):', response.data.data);

      // ✅ Do NOT redirect to payment portal even if paymentRedirect exists
      // Instead, always go to the Pay Later page
      return response.data.data;
    } else {
      console.error('Subscription creation failed:', response.data.message);
      throw new Error(response.data.message);
    }
  } catch (error) {
    console.error('Error during subscription creation:', error);
    throw error;
  }
};



// New function to show User Confirmation modal
export const showConfirmationModal = (setIsModalVisible) => {
  setIsModalVisible(true); // This will trigger the modal to be visible
};


// Function to fetch options based on the selected optionType
export const fetchOptions = async (option_type) => {
  try {
    const response = await apiClient.get(`/registration-details/options?option_type=${option_type}`);
    return response.data.data.map(option => ({
      id: option.id,
      name: option.value,
    }));
  } catch (error) {
    console.error('Error fetching options:', error);
    return [];
  }
};


// Function to resend OTP
export const resendOtp = async (confirmationDetails) => {
  console.log('Payload sent to confirmation API:', confirmationDetails);

  try {
    const response = await apiClient.post('/user/confirmation', confirmationDetails);

    if (response.data.success) {
      console.log('User confirmed successfully:', response.data.message);
      return response.data.message;
    } else {
      console.error('User confirmation failed:', response.data.message);
      throw new Error(response.data.message);
    }
  } catch (error) {
    console.error('Error during user confirmation:', error);
    throw error;
  }
};


//Function for resending OTP (Forgot password and Portal password reset)
export const resendConfirmationOTP = async (portalEndCustomerId) => {
  try {
    const response = await apiClient.get(`/user/resend-confirmation/${portalEndCustomerId}`);

    if (response.data?.success) {
      return { success: true, message: response.data.message || "OTP resent successfully." };
    }

    return {
      success: false,
      message: response.data?.message || "Failed to resend confirmation OTP.",
    };
  } catch (error) {
    const detail = error?.response?.data?.detail;
    const message = Array.isArray(detail)
      ? detail.map((e) => e.msg).join(" | ")
      : error?.response?.data?.message || error.message;

    return { success: false, message };
  }
};

// Function for profile picture
export const uploadProfilePicture = (event, setProfilePicture) => {
  if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      const reader = new FileReader();
      reader.onload = () => {
          if (reader.result) {
              setProfilePicture(reader.result); // No TypeScript `as` syntax
          }
      };
      reader.readAsDataURL(file);
  }
};


export const login = async (username, password, navigate) => {
  const data = new URLSearchParams();
  data.append("grant_type", "password");
  data.append("username", username);
  data.append("password", password);

  try {
    const response = await apiClient.post(
      "user/login",
      data,
      {
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
      }
    );

    if (response.data && response.data.success) {
      localStorage.setItem("accessToken", response.data.access_token);
      localStorage.setItem(
        "portalEndCustomerId",
        response.data.portal_end_customer_id
      );

      // Tell the whole app the user is now logged in (navbar, header,
      // protected routes, etc.) so nothing needs a page refresh.
      notifyAuthChanged();

      // Only navigate if a navigate function was actually passed in.
      // Callers like ReferralLoginModal intentionally omit it so the
      // user stays on the current page (e.g. /referral) after login,
      // instead of being bounced to /profile.
      if (typeof navigate === "function") {
        navigate("/profile");
      }

      return { success: true };
    }

    return {
      success: false,
      detail: response.data?.detail,
      message: response.data?.message || "Invalid credentials",
    };
  } catch (error) {
    console.error("Error during login:", error);

    return {
      success: false,
      detail: error.response?.data?.detail,
      message: getErrorMessage(error),
    };
  }
};


// Log the user out and tell the whole app straight away.
// Use this everywhere you currently do localStorage.removeItem(...) on logout.
export const logout = () => {
  try {
    localStorage.removeItem("accessToken");
    localStorage.removeItem("portalEndCustomerId");
  } catch (err) {
    console.error("Logout: localStorage access blocked:", err);
  }
  notifyAuthChanged();
};



//Function for Forgot Password
export const forgotPassword = async (email, mobileNumber) => {
  try {
    // Build URL with both fields (even if empty)
    const url = `${PORTAL_BASE_URL}/user/forgot-password?email=${encodeURIComponent(email || '')}&mobile_number=${encodeURIComponent(mobileNumber || '')}`;

    // No body; headers go in the 3rd argument (config), not the 2nd (body)
    const response = await axios.post(url, null, {
      headers: {
        'Content-Type': 'application/json',
        'x-portal-tenant-host': 'ftthsa',
      },
    });

    if (response.data.success) {
      return { success: true, message: response.data.message };
    } else {
      return { success: false, message: response.data.message };
    }
  } catch (error) {
    console.error('Error during forgot password process:', error);
    return {
      success: false,
      message: error.response?.data?.detail || 'An error occurred. Please try again.',
    };
  }
};




// Function to confirm and reset a user's forgotten password
export const confirmForgotPassword = async (data) => {
  try {
    const response = await axios.post(
      `${PORTAL_BASE_URL}/user/forgot-password-confirmation`,
      {
        confirmation_email: data.email,
        confirmation_mobile_number: data.mobileNumber,
        confirmation_pin: data.otp,
        new_password: data.newPassword,
      },
      {
        headers: {
          'Content-Type': 'application/json',
          'x-portal-tenant-host': 'ftthsa',
        },
      }
    );

    if (response.data.success) {
      return {
        success: true,
        message: response.data.message,
      };
    } else {
      return {
        success: false,
        message: response.data.message,
      };
    }
  } catch (error) {
    console.error('Error confirming forgot password:', error);
    return {
      success: false,
      message:
        error.response?.data?.detail || 'An error occurred. Please try again.',
    };
  }
};


export const requestPasswordResetOTP = async (email, mobileNumber) => {
  try {
    const portalEndCustomerId = localStorage.getItem("portalEndCustomerId");
    if (!portalEndCustomerId) {
      return { success: false, message: "User ID not found in local storage." };
    }

    const params = new URLSearchParams();
    params.append("portal_end_customer_id", portalEndCustomerId); // 🔐 required param
    params.append("email", email || "");
    params.append("mobile_number", mobileNumber || "");

    const url = `/user/reset-password?${params.toString()}`;

    const response = await apiClient.post(url);

    if (response.data?.success) {
      return { success: true, message: response.data.message };
    }

    return {
      success: false,
      message: response.data?.message || "OTP request failed.",
    };
  } catch (error) {
    const detail = error?.response?.data?.detail;
    const flatMessage = Array.isArray(detail)
      ? detail.map((e) => e.msg).join(" | ")
      : error?.response?.data?.message || error.message;

    return { success: false, message: flatMessage };
  }
};


// Function to confirm password reset
export const confirmPasswordReset = async ({ confirmation_pin, new_password, old_password }) => {
  try {
    const url = '/user/reset-password-confirmation';  // Adjust if backend path differs
    const payload = {
      confirmation_pin,
      new_password,
      old_password,
    };

    const response = await apiClient.post(url, payload);

    if (response.data && response.data.success) {
      return { success: true, message: response.data.message };
    }
    return { success: false, message: response.data?.message || "Password reset failed." };
  } catch (error) {
    console.error("Error confirming password reset:", error);
    return {
      success: false,
      message:
        error?.response?.data?.detail ||
        error?.response?.data?.message ||
        error.message ||
        "An unexpected error occurred.",
    };
  }
};


//Function to fetch User Profile
export const fetchUserProfile = async (portal_end_customer_id) => {
  console.log("Fetching profile for ID:", portal_end_customer_id);
  try {
    const response = await apiClient.get(`/user/${portal_end_customer_id}`);
    const userProfile = response.data.data;

    return {
      firstName: userProfile.first_name,
      lastName: userProfile.last_name,
      identificationReference: userProfile.identification_reference,
      identificationType: userProfile.identification_type,
      mobileNumber: userProfile.mobile_number,
      email: userProfile.email,
      id: userProfile.id,
      active: userProfile.active,
      confirmed: userProfile.confirmed,
      alternateContactNumber: userProfile.alternate_contact_number,
      complexBuilding: userProfile.complex_building,
      unitNumber: userProfile.unit_number,
      streetAddress: userProfile.street_address,
      postalAddress: userProfile.postal_address,
      suburb: userProfile.suburb,
      city: userProfile.city,
      province: userProfile.province,
      postalCode: userProfile.postal_code,
    };

  } catch (error) {
    console.error('Error fetching user profile:', error);
    throw new Error('Error fetching data');
  }
};

// Function to fetch User Subscriptions
export const fetchUserSubscriptions = async (portal_end_customer_id) => {
  console.log("Fetching subscriptions for ID:", portal_end_customer_id);

  try {
    const response = await apiClient.get(`/subscriptions/customer/${portal_end_customer_id}`);
    
    // Check if the response was successful
    if (!response.data.success) {
      throw new Error(response.data.message || "Failed to fetch subscriptions");
    }

    const subscriptions = response.data.data || [];

    return subscriptions.map((sub) => ({
      id: sub.id,
      reference: sub.subscription_reference,
      status: sub.subscription_status,
      accountStatus: sub.account_status,
      autoRenew: sub.billing_auto_renew,
      paymentMethod: sub.preferred_payment_method,
      activated: sub.subscription_activated,
      activationDate: sub.date_activated,
      nextBillingDate: sub.date_billing_next,
      accountReference: sub.subscription_account_reference,
      portalProduct: sub.portal_product
        ? {
            name: sub.portal_product.name,
            description: sub.portal_product.description,
            speedDownMbps: sub.portal_product.speed_down_mbps,
            speedUpMbps: sub.portal_product.speed_up_mbps,
            price: sub.portal_product.recurring_price_incl,
            currency: sub.portal_product.currency,
            renewalType: sub.portal_product.renewal_type,
            durationDays: sub.portal_product.duration_days,
          }
        : null, // Handle cases where portal_product is missing
    }));
  } catch (error) {
    console.error("Error fetching subscriptions:", error);
    return []; // Return an empty array in case of error
  }
};

// Function to fetch User Subscription Countdown 
export const fetchSubscriptionCountdown = async (portalSubscriptionId) => {
  try {
    if (!Number.isInteger(portalSubscriptionId)) {
      return {
        success: false,
        message: "portalSubscriptionId must be an integer",
      };
    }

    const response = await apiClient.get(
      `/subscriptions/${portalSubscriptionId}/countdown`
    );

    // Expected: { success: boolean, message: string, data: object }
    return {
      success: response.data?.success ?? true,
      message: response.data?.message ?? "OK",
      data: response.data?.data,
      status: response.status,
    };
  } catch (error) {
    const status = error.response?.status;
    const backend = error.response?.data;

    // 422 Validation Error format: { detail: [...] }
    if (status === 422) {
      return {
        success: false,
        message: "Validation error",
        detail: backend?.detail,
        status,
      };
    }

    return {
      success: false,
      message: backend?.message || backend?.detail || "Failed to fetch countdown",
      detail: backend?.detail,
      status,
    };
  }
};


// Function to fetch transaction by portal_transaction_id
export const fetchTransactionById = async (portal_transaction_id) => {
  console.log("Fetching transaction for ID:", portal_transaction_id);

  try {
    const response = await apiClient.get(`/transactions/${portal_transaction_id}`);

    // Check if the response was successful
    if (!response.data.success) {
      throw new Error(response.data.message || "Failed to fetch transaction");
    }

    const transaction = response.data.data;

    return {
      id: transaction.id,
      portalEndCustomerId: transaction.portal_end_customer_id,
      portalSubscriptionId: transaction.portal_subscription_id,
      portalProductId: transaction.portal_product_id,
      transactionDate: transaction.transaction_date,
      transactionDateDue: transaction.transaction_date_due,
      amountExcl: transaction.amount_excl,
      amountIncl: transaction.amount_incl,
      currency: transaction.currency,
      transactionType: transaction.transaction_type,
      status: transaction.status,
      reference: transaction.reference,
      externalReference: transaction.refrence_external,
      description: transaction.transaction_description,
      method: transaction.transaction_method,
    };
  } catch (error) {
    console.error("Error fetching transaction:", error);
    return null;
  }
};

//------------------------------------------------------

// Fetch transactions for a specific subscription
export const fetchTransactionsBySubscription = async (portalSubscriptionId) => {
  try {
    const response = await apiClient.get(`/subscriptions/${portalSubscriptionId}/transactions`);
    if (response.data.success) {
      return response.data.data.map((transaction) => ({
        id: transaction.id,
        portalSubscriptionId: transaction.portal_subscription_id,
        transactionDate: transaction.transaction_date,
        transactionDateDue: transaction.transaction_date_due,
        amountExcl: transaction.amount_excl,
        amountIncl: transaction.amount_incl,
        currency: transaction.currency,
        transactionType: transaction.transaction_type,
        status: transaction.status,
        reference: transaction.reference,
        description: transaction.transaction_description,
        method: transaction.transaction_method,
      }));
    } else {
      throw new Error(response.data.message || "Failed to fetch transactions");
    }
  } catch (error) {
    console.error("Error fetching transactions by subscription:", error);
    throw error;
  }
};

// Fetch transactions for a specific customer
export const fetchTransactionsByCustomer = async (portalEndCustomerId) => {
  try {
    const response = await apiClient.get(`/transactions/customer/${portalEndCustomerId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching transactions by customer:', error);
    throw error;
  }
};

// Fetch details of a specific transaction
export const fetchTransactionDetails = async (portalTransactionId) => {
  try {
    const response = await apiClient.get(`/transactions/${portalTransactionId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching transaction details:', error);
    throw error;
  }
};


// Pay a pending transaction 
export const payTransaction = async (
  portalEndCustomerId,
  portalTransactionId,
  paymentMethod,
  tokeniseCard = false
) => {
  try {
    const url =
      `/transactions/${portalEndCustomerId}/pay/${portalTransactionId}` +
      `?payment_method_override=${encodeURIComponent(paymentMethod)}` +
      `&payment_method_tokenise=${tokeniseCard}`;

    const response = await apiClient.get(url);

    if (!response.data?.success) {
      return {
        success: false,
        message: response.data?.message || "Payment failed",
        paymentRedirect: null,
      };
    }

    return {
      success: true,
      message: response.data?.message,
      paymentRedirect: response.data?.paymentRedirect,
    };
  } catch (error) {
    console.error("Error processing transaction payment:", error);
    return {
      success: false,
      message: getErrorMessage(error),
      paymentRedirect: null,
    };
  }
};




// Function to regrade (upgrade / downgrade) a subscription
export const regradeSubscription = async (portalSubscriptionId, regradePortalProductId) => {
  try {
    // ✨ product ID only in the POST body
    const url = `/subscriptions/${encodeURIComponent(portalSubscriptionId)}/regrade`;

    const response = await apiClient.post(url, {
      regrade_portal_product_id: regradePortalProductId,
    });

    if (response.data.success) {
      return {
        success: true,
        message: response.data.message,
        data: response.data.data,
      };
    }

    return { success: false, message: response.data.message };
  } catch (error) {
    console.error("Error during subscription regrade:", error);
    return {
      success: false,
      message: getErrorMessage(error),
    };
  }
};


// Function to cancel a subscription with a cancellation reason
export const cancelSubscription = async (portalSubscriptionId, cancellationReason) => {
  try {
    // Construct the URL with the path parameter
    const url = `/subscriptions/${encodeURIComponent(portalSubscriptionId)}/cancel`;

    // Send a POST request with the reason in the body
    const response = await apiClient.post(url, {
      cancellation_reason: cancellationReason,
    });

    if (response.data.success) {
      console.log('Subscription canceled successfully:', response.data.data);
      return {
        success: true,
        message: response.data.message,
        data: response.data.data,
      };
    } else {
      console.error('Failed to cancel subscription:', response.data.message);
      return {
        success: false,
        message: response.data.message,
      };
    }
  } catch (error) {
    console.error('Error during subscription cancellation:', error);
    return {
      success: false,
      message: getErrorMessage(error),
    };
  }
};



// Create manual invoice for a subscription
export const createManualInvoice = async (subscriptionId) => {
  try {
    const url = `billing/${subscriptionId}/manual-invoice`;
    const response = await apiClient.post(url);

    if (!response.data.success) {
      throw new Error(response.data.message || 'Manual invoice creation failed');
    }

    return response.data;
  } catch (error) {
    console.error('Error creating manual invoice:', error);
    return {
      success: false,
      error: getErrorMessage(error),
    };
  }
};


export const generateSubscriptionStatement = async (subscriptionId) => {
  try {
    const response = await apiClient.get(`/subscriptions/${subscriptionId}/statement`, {
      //params: { subscription_id: subscriptionId }, // optional redundancy
      responseType: "blob",
    });

    const disposition = response.headers["content-disposition"] || "";
    const match = disposition.match(/filename="?([^"]+)"?/);
    const filename = match ? match[1] : "statement.pdf";

    return {
      success: true,
      blob: response.data,
      filename,
      contentType: response.headers["content-type"] || "application/pdf",
    };
  } catch (error) {
    let errorMessage = "Failed to generate subscription statement";

    try {
      // Convert blob error to JSON if the server sent error info in blob format
      const errorData = await error.response.data.text(); // read blob as text
      const parsed = JSON.parse(errorData);
      errorMessage = parsed.message || parsed.detail || errorMessage;
    } catch {
      // fallback if it's not JSON or parse failed
      errorMessage = getErrorMessage(error) || errorMessage;
    }

    return {
      success: false,
      message: errorMessage,
    };
  }
};



//Function to get Router Details 
export const getSubscriptionDetails = async (portalSubscriptionId) => {
  try {
    const resp = await apiClient.get(
      `/subscriptions/details/${encodeURIComponent(portalSubscriptionId)}`
    );

    if (resp?.data?.success) {
      // {wifi_ssid , wifi_password}
      return { success: true, data: resp.data.data, message: resp.data.message };
    }

    return {
      success: false,
      message: resp?.data?.message || "Failed to fetch router details.",
    };
  } catch (err) {
    const detail = err?.response?.data?.detail;
    const message = Array.isArray(detail)
      ? detail.map((d) => d.msg).join(" | ")
      : err?.response?.data?.message || err.message;

    return { success: false, message };
  }
};


//Function to update router details 
export const updateRouterDetails = async (portalSubscriptionId, wifiSsid, wifiPassword) => {
  try {
    const resp = await apiClient.post(
      `/subscriptions/details/${encodeURIComponent(portalSubscriptionId)}`,
      {
        wifi_ssid: wifiSsid,   // Request payload
        wifi_password: wifiPassword
      },
      {
        headers: { "Content-Type": "application/json" }
      }
    );


    return {
      success: resp.data.success, // Expected response shape
      message: resp.data.message,
      data: resp.data.data        // Contains updated wifi_ssid and wifi_password
    };
  } catch (err) {
    const detail = err?.response?.data?.detail;
    const message = Array.isArray(detail)
      ? detail.map((d) => d.msg).join(" | ")
      : err?.response?.data?.message || err.message;

    return { success: false, message };
  }
};


// Function to GET announcements for the portal
export const fetchAnnouncements = async ({ offset = 0, limit = 100 } = {}) => {
  try {
    const response = await apiClient.get("/announcements", {
      params: { offset, limit },
    });

    // API shape:
    // { success, message, data: [...], count }
    return {
      success: response.data?.success === true,
      message: response.data?.message,
      data: Array.isArray(response.data?.data) ? response.data.data : [],
      count: response.data?.count ?? 0,
    };
  } catch (error) {
    return {
      success: false,
      message:
        error.response?.data?.message ||
        error.response?.data?.detail ||
        "Failed to fetch announcements",
      data: [],
      count: 0,
    };
  }
};

// Optional cache buster if you want it
export const refreshAnnouncementsCache = async ({ offset = 0, limit = 100 } = {}) => {
  return fetchAnnouncements({ offset, limit });
};

// Get specific announcement by ID
export const getAnnouncementById = async (announcementId) => {
  try {
    const response = await apiClient.get(`/announcements/${announcementId}`);
    return {
      success: true,
      data: response.data.data || response.data,
    };
  } catch (error) {
    console.error('Error fetching announcement:', error);
    return {
      success: false,
      message: getErrorMessage(error),
      data: null,
    };
  }
};


// Function to recharge a subscription
export const rechargeSubscription = async (subscriptionId) => {
  try {
    const url = `billing/subscription/${subscriptionId}/recharge`;

    const response = await apiClient.get(url);

    if (response.data.success) {
      return {
        success: true,
        message: response.data.message,
        paymentRedirect: response.data.paymentRedirect,
      };
    }

    return { success: false, message: response.data.message };
  } catch (error) {
    console.error("Error during subscription recharge:", error);
    return {
      success: false,
      message: getErrorMessage(error),
    };
  }
};


// =====================================================================
// REFERRALS
// Backend group: /referrals/*  (all relative to apiClient's baseURL,
// i.e. https://stm-dev.intentio.co.za/api/portal/)
// =====================================================================

// Function to fetch the logged-in user's referral history
// GET /referrals/referrals
// Swagger sample returns a raw array (not wrapped in {success, data}), but
// this defensively also handles a wrapped { data: [...] } shape in case the
// backend later aligns it with the rest of the endpoints in this file.
// If you're seeing an empty "no referrals yet" state on the history page
// but the Network tab shows a non-empty response body, check the console —
// this will log the exact shape it received so you can see what didn't
// match.
export const fetchReferrals = async () => {
  try {
    const response = await apiClient.get('/referrals/referrals');

    const raw = Array.isArray(response.data)
      ? response.data
      : Array.isArray(response.data?.data)
      ? response.data.data
      : null;

    if (raw === null) {
      console.error(
        'Unexpected /referrals/referrals response shape — expected an array ' +
          'or { data: [...] }, got:',
        response.data
      );
      return {
        success: false,
        message: 'Unexpected response format from referrals endpoint.',
        data: [],
      };
    }

    return {
      success: true,
      data: raw.map((r) => ({
        id: r.id,
        referralCode: r.referral_code,
        referrerPortalAccountsId: r.referrer_portal_accounts_id,
        referredName: r.referred_name,
        referredMobileNumber: r.referred_mobile_number,
        status: r.status,
        redeemedPortalAccountsId: r.redeemed_portal_accounts_id,
        redeemedPortalSubscriptionId: r.redeemed_portal_subscription_id,
        redeemedAt: r.redeemed_at,
        consentAccepted: r.consent_accepted,
        consentAcceptedAt: r.consent_accepted_at,
      })),
    };
  } catch (error) {
    console.error('Error fetching referrals:', error);
    return {
      success: false,
      message: getErrorMessage(error),
      data: [],
    };
  }
};

// Function to create a new referral for the logged-in user
// POST /referrals/referrals
// Payload: { referred_name, referred_mobile_number, consent_accepted }
// NOTE: the referrer is inferred server-side from the Bearer token, so no
// referrer fields are sent here. The DOCUMENTED endpoint only accepts
// name, mobile number and consent — area/address/building/package are NOT
// stored by this endpoint.
//
// FIX (frontend audit): the UI collects a friend's email and, per the BRS
// door-sticker campaign, should also carry the QR/campaign identifier the
// visitor scanned in on (BR21). Neither was ever forwarded here before.
// This function now accepts a 4th, optional `extra` object and includes
// any of those keys in the payload if present — but until the /referrals/
// referrals schema on the backend is confirmed to store referred_email /
// campaign_id / building_id / qr_code_id, treat this as best-effort only.
// FastAPI/Pydantic usually ignores unrecognised JSON keys by default, so
// this should be safe to ship, but confirm with Max (or check a captured
// referral row) that these fields are actually being persisted rather
// than silently dropped before relying on them for reporting.
export const createReferral = async (
  referredName,
  referredMobileNumber,
  consentAccepted,
  extra = {}
) => {
  try {
    const payload = {
      referred_name: referredName,
      referred_mobile_number: referredMobileNumber,
      consent_accepted: consentAccepted,
    };

    if (extra.referredEmail) payload.referred_email = extra.referredEmail;
    if (extra.campaignId) payload.campaign_id = extra.campaignId;
    if (extra.buildingId) payload.building_id = extra.buildingId;
    if (extra.qrCodeId) payload.qr_code_id = extra.qrCodeId;

    const response = await apiClient.post('/referrals/referrals', payload);

    const data = response.data || {};

    return {
      success: true,
      data: {
        id: data.id,
        referralCode: data.referral_code,
        referrerPortalAccountsId: data.referrer_portal_accounts_id,
        referredName: data.referred_name,
        referredMobileNumber: data.referred_mobile_number,
        status: data.status,
        consentAccepted: data.consent_accepted,
        consentAcceptedAt: data.consent_accepted_at,
      },
    };
  } catch (error) {
    console.error('Error creating referral:', error);
    return {
      success: false,
      message: getErrorMessage(error),
    };
  }
};

// Function to validate a referral code (used on the referred friend's side,
// e.g. during their own sign-up flow to confirm the code and show who
// referred them)
// POST /referrals/referrals/validate
// Payload: { referral_code, mobile_number }
export const validateReferralCode = async (referralCode, mobileNumber) => {
  try {
    const response = await apiClient.post('/referrals/referrals/validate', {
      referral_code: referralCode,
      mobile_number: mobileNumber,
    });

    return {
      success: true,
      valid: response.data?.valid,
      referrerName: response.data?.referrer_name,
    };
  } catch (error) {
    console.error('Error validating referral code:', error);
    return {
      success: false,
      message: getErrorMessage(error),
    };
  }
};

// Function to set/update the logged-in user's referral reward payout
// preference at the account level.
// PATCH /referrals/account/referral-preferences
// Payload: { reward_type, bank_details?: { account_holder_name,
//            account_number, bank_name, branch_code, iban } }
//
// reward_type is a plain string on this endpoint with no documented enum.
// This app uses it as:
//   "cash"   -> Cash Payout (bank_details required)
//   "credit" -> Credit applied to the customer's FTTHSA account (no
//               bank_details needed — pass bankDetails = null)
export const updateReferralPreferences = async (rewardType, bankDetails) => {
  try {
    const payload = { reward_type: rewardType };

    if (bankDetails) {
      payload.bank_details = {
        account_holder_name: bankDetails.accountHolderName || '',
        account_number: bankDetails.accountNumber || '',
        bank_name: bankDetails.bankName || '',
        branch_code: bankDetails.branchCode || '',
        iban: bankDetails.iban || '',
      };
    }

    const response = await apiClient.patch('/referrals/account/referral-preferences', payload);

    return {
      success: true,
      rewardType: response.data?.reward_type,
      bankDetailsOnFile: response.data?.bank_details_on_file,
      accountNumberLast4: response.data?.account_number_last4,
    };
  } catch (error) {
    console.error('Error updating referral preferences:', error);
    return {
      success: false,
      message: getErrorMessage(error),
    };
  }
};